import { FC } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import DatePickerCustomHeaderOneMonth from "../../DatePickerCustomHeaderOneMonth";
import DatePickerCustomHeaderTwoMonth from "../../DatePickerCustomHeaderTwoMonth";
import { calendarHeader } from "../../CarPageB/calendarParts";

interface DatePickerModalProps {
  showDatePicker: "start" | "end" | null;
  tempSelectedDate: Date | null;
  handleModalDateChange: (date: Date | null) => void;
  handleModalSave: () => void;
  handleModalClose: () => void;
  monthsToShow:  number;
  calendarViewDate: Date | null;
  startDate: Date | null;
  isDateSelectable: (date:  Date) => boolean;
  renderDayContents: (day: number, date?:  Date) => React.ReactNode;
  handleMonthChange: (date: Date) => void;
  isLoading?: boolean;
  /** 1NOW-11176: Design B's plain version, above the picker's own modal. */
  look?: "b";
}

const DatePickerModal:  FC<DatePickerModalProps> = ({
  showDatePicker,
  tempSelectedDate,
  handleModalDateChange,
  handleModalSave,
  handleModalClose,
  monthsToShow,
  calendarViewDate,
  startDate,
  isDateSelectable,
  renderDayContents,
  handleMonthChange,
  isLoading = false,
  look,
}) => {
  if (!showDatePicker) return null;

  if (look === "b") {
    const startPick = showDatePicker === "start";
    const heading = startPick ? "Choose a start date" : "Choose an end date";
    return (
      <div className="cpb fixed inset-0 z-[90] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={heading}>
        <div className="absolute inset-0 bg-black/40" onClick={isLoading ? undefined : handleModalClose} />
        <div className="relative flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-card-color text-text-color shadow-[0_8px_30px_rgba(0,0,0,0.2)] sm:max-w-3xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex shrink-0 items-center justify-between px-6 pb-1 pt-5">
            <h2 className="text-lg font-semibold">{heading}</h2>
            <button type="button" onClick={isLoading ? undefined : handleModalClose} aria-label="Close" disabled={isLoading} className="flex h-9 w-9 items-center justify-center rounded-full border border-text-color/15 hover:bg-text-color/5 disabled:opacity-50">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className={`cpb-cal min-h-0 flex-1 overflow-y-auto px-6 py-3 ${isLoading ? "pointer-events-none opacity-60" : ""}`}>
            <div className="flex justify-center">
              <DatePicker
                selected={tempSelectedDate}
                onChange={handleModalDateChange}
                monthsShown={monthsToShow}
                showPopperArrow={false}
                inline
                minDate={new Date()}
                openToDate={calendarViewDate || startDate || new Date()}
                filterDate={isDateSelectable}
                renderCustomHeader={calendarHeader(monthsToShow)}
                renderDayContents={renderDayContents}
                onMonthChange={handleMonthChange}
              />
            </div>
          </div>
          <div className="shrink-0 border-t border-text-color/10 px-6 pb-5 pt-4">
            <div className="flex justify-end">
              <button type="button" onClick={handleModalSave} disabled={!tempSelectedDate} className="h-11 rounded-lg bg-primary-brand px-8 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-700 disabled:opacity-50">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isStart = showDatePicker === "start";
  const title = isStart ? "Choose a start date" : "Choose an end date";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/10 backdrop-blur-sm rounded-xl"
        onClick={isLoading ? undefined : handleModalClose}
      />

      {/* Modal */}
      <div 
        className="relative bg-card-color rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card-color z-10 px-6 py-4 border-b border-secondary-100">
          <div className="flex items-center justify-between">
            <button
              onClick={isLoading ? undefined : handleModalClose}
              disabled={isLoading}
              className="p-2 hover:bg-secondary-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="w-5 h-5 text-text-color" />
            </button>
            <h2 className="text-lg font-semibold text-text-color">{title}</h2>
            <div className="w-9" /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Calendar */}
        <div className="p-6 relative">
          {isLoading && (
            <div className="absolute inset-0 bg-card-color/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-10 h-10 border-4 border-primary-brand border-t-transparent rounded-full animate-spin"></div>
               
              </div>
            </div>
          )}
          
          <div className="flex justify-center">
            <DatePicker
              selected={tempSelectedDate}
              onChange={handleModalDateChange}
              monthsShown={monthsToShow}
              showPopperArrow={false}
              inline
              minDate={new Date()}
              openToDate={calendarViewDate || startDate || new Date()}
              filterDate={isDateSelectable}
              renderCustomHeader={(p) => (
                <>
                  <div className="block sm:hidden">
                    <DatePickerCustomHeaderOneMonth {... p} />
                  </div>
                  <div className="hidden sm:block">
                    <DatePickerCustomHeaderTwoMonth {...p} />
                  </div>
                </>
              )}
              renderDayContents={renderDayContents}
              onMonthChange={handleMonthChange}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card-color border-t border-secondary-100 px-6 py-4">
          <div className="flex items-center justify-end">
            <button
              onClick={handleModalSave}
              disabled={! tempSelectedDate}
              className="px-8 py-3 bg-primary-brand hover:bg-primary-700 text-on-primary font-semibold rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;